import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { ref, get, set } from 'firebase/database';
import { database } from '../config/firebase';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';

interface Manager {
  phonenumber: string;
  username: string;
  password: string;
  grade: string;
  appname: string;
  block: string;
  initdate: string;
  referralcode: string;
}

interface DepositData {
  phonenumber: string;
  username: string;
  currentDeposit: number;
  lastUpdateDate: string;
}

interface DepositLog {
  timestamp: string;
  type: 'deposit' | 'withdraw';
  amount: number;
  date: string;
  reason?: string;
  balance?: number;
}

interface ChungcheongDepositManagerProps {
  open: boolean;
  onClose: () => void;
  selectedManager: Manager | null;
  currentUser?: any; // 현재 로그인한 사용자 정보 추가
}

const ChungcheongDepositManager: React.FC<ChungcheongDepositManagerProps> = ({
  open,
  onClose,
  selectedManager,
  currentUser,
}) => {
  const [depositData, setDepositData] = useState<DepositData | null>(null);
  const [depositLogs, setDepositLogs] = useState<DepositLog[]>([]);
  const [depositAmount, setDepositAmount] = useState<string>('');

  // localmaster + smlink_M2_충청 사용자인지 확인
  const isLocalmasterChungcheong = currentUser?.grade === 'localmaster' && currentUser?.appname === 'smlink_M2_충청';

  // 예치금 데이터 조회
  const fetchDepositData = async (phonenumber: string) => {
    try {
      // deposit 테이블에서 데이터 가져오기
      const depositRef = ref(database, `deposit/${phonenumber}`);
      const depositSnapshot = await get(depositRef);
      
      if (depositSnapshot.exists()) {
        const data = depositSnapshot.val();
        setDepositData(data);
      } else {
        // 데이터가 없는 경우 0으로 초기화
        setDepositData({
          phonenumber: phonenumber,
          username: selectedManager?.username || '',
          currentDeposit: 0,
          lastUpdateDate: ''
        });
      }

      // depositlogs 테이블에서 로그 가져오기
      const logsRef = ref(database, `depositlogs/${phonenumber}`);
      const logsSnapshot = await get(logsRef);
      
      if (logsSnapshot.exists()) {
        const logsData = logsSnapshot.val();
        const logs = Object.entries(logsData).map(([timestamp, logData]: [string, any]) => ({
          timestamp,
          type: logData.type,
          amount: logData.amount,
          date: logData.date,
          reason: logData.reason || '',
          balance: logData.balance || 0
        })).sort((a, b) => b.timestamp.localeCompare(a.timestamp)); // 최신순 정렬
        setDepositLogs(logs);
      } else {
        setDepositLogs([]);
      }
    } catch (error) {
      console.error('예치금 데이터 조회 실패:', error);
      // 오류 발생 시에도 0으로 초기화
      setDepositData({
        phonenumber: phonenumber,
        username: selectedManager?.username || '',
        currentDeposit: 0,
        lastUpdateDate: ''
      });
      setDepositLogs([]);
    }
  };

  // 예치금 저장
  const handleDepositSave = async () => {
    if (!selectedManager || !depositAmount || isNaN(Number(depositAmount))) {
      alert('올바른 금액을 입력하세요.');
      return;
    }

    // 입력값을 정수로 변환
    const amount = Math.floor(Number(depositAmount));
    if (amount <= 0) {
      alert('0보다 큰 금액을 입력하세요.');
      return;
    }

    try {
      const now = new Date();
      const timestamp = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0');

      const dateString = now.getFullYear() + '-' +
        String(now.getMonth() + 1).padStart(2, '0') + '-' +
        String(now.getDate()).padStart(2, '0') + ' ' +
        String(now.getHours()).padStart(2, '0') + ':' +
        String(now.getMinutes()).padStart(2, '0') + ':' +
        String(now.getSeconds()).padStart(2, '0');

      const currentDeposit = Math.floor(depositData?.currentDeposit || 0);
      const newDeposit = Math.floor(currentDeposit + amount);

      // deposit 테이블 업데이트
      const depositUpdateData = {
        phonenumber: selectedManager.phonenumber,
        username: selectedManager.username,
        currentDeposit: newDeposit,
        lastUpdateDate: dateString
      };

      await set(ref(database, `deposit/${selectedManager.phonenumber}`), depositUpdateData);

      // depositlogs 테이블에 로그 추가
      const logData = {
        type: 'deposit',
        amount: amount,
        date: dateString,
        balance: newDeposit
      };

      await set(ref(database, `depositlogs/${selectedManager.phonenumber}/${timestamp}`), logData);

      // 상태 업데이트
      setDepositData(depositUpdateData);
      setDepositLogs(prev => [{
        timestamp,
        type: 'deposit',
        amount,
        date: dateString,
        balance: newDeposit
      }, ...prev]);

      setDepositAmount('');
      alert('예치금이 성공적으로 입금되었습니다.');
    } catch (error) {
      console.error('예치금 저장 실패:', error);
      alert('예치금 저장에 실패했습니다.');
    }
  };

  // 다이얼로그 열릴 때 예치금 데이터 조회
  React.useEffect(() => {
    if (open && selectedManager) {
      fetchDepositData(selectedManager.phonenumber);
    }
  }, [open, selectedManager]);

  // 다이얼로그 닫기 처리
  const handleClose = () => {
    setDepositData(null);
    setDepositLogs([]);
    setDepositAmount('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>충청 예치금 관리</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            전화번호: {formatPhoneNumber(selectedManager?.phonenumber || '')}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            이름: {selectedManager?.username}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            현재 예치금: {depositData?.currentDeposit?.toLocaleString() || '0'}원
          </Typography>
          {depositData?.lastUpdateDate && (
            <Typography variant="body2" color="text.secondary">
              마지막 업데이트: {depositData.lastUpdateDate}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            label="예치금 입금"
            type="number"
            value={depositAmount}
            onChange={(e) => {
              // 입력값을 정수로 변환
              const value = e.target.value;
              const intValue = value ? Math.floor(Number(value)) : '';
              setDepositAmount(intValue.toString());
            }}
            fullWidth
            margin="normal"
            placeholder="입금할 금액을 입력하세요"
            inputProps={{
              step: 1, // 정수 단위로만 입력 가능
              min: 1  // 최소값 1
            }}
            disabled={isLocalmasterChungcheong} // localmaster + smlink_M2_충청 사용자는 입력 불가
          />
          {isLocalmasterChungcheong && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              * localmaster는 예치금 히스토리만 확인 가능합니다.
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            예치금 히스토리
          </Typography>
          <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            {depositLogs.length > 0 ? (
              <List dense>
                {depositLogs.map((log, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={
                        <Box>
                          {/* 모바일 뷰 - 세로 배치 */}
                          <Box sx={{ 
                            display: { xs: 'flex', md: 'none' }, 
                            flexDirection: 'column',
                            gap: 1
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">{log.date}</Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: log.type === 'deposit' ? '#4caf50' : '#f44336',
                                  fontWeight: 600
                                }}
                              >
                                {log.type === 'deposit' ? '+' : '-'}{log.amount.toLocaleString()}원
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                                }}
                              >
                                {log.type === 'deposit' ? '입금' : '출금'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                잔액: {(log.balance ?? 0).toLocaleString()}원
                              </Typography>
                            </Box>
                          </Box>

                          {/* PC 뷰 - 가로 배치 */}
                          <Box sx={{ 
                            display: { xs: 'none', md: 'flex' }, 
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <Box sx={{ width: '140px' }}>{log.date}</Box>
                            <Box sx={{ 
                              width: '60px', 
                              textAlign: 'center', 
                              color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                            }}>
                              {log.type === 'deposit' ? '입금' : '출금'}
                            </Box>
                            <Box sx={{ 
                              width: '120px', 
                              textAlign: 'right', 
                              fontWeight: 600,
                              color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                            }}>
                              {log.type === 'deposit' ? '+' : '-'}{log.amount.toLocaleString()}원
                            </Box>
                            <Divider orientation="vertical" flexItem sx={{ mx: 2, display: { xs: 'none', md: 'block' } }} />
                            <Box sx={{ width: '120px', textAlign: 'right', color: 'text.secondary' }}>
                              잔액: {(log.balance ?? 0).toLocaleString()}원
                            </Box>
                          </Box>
                        </Box>
                      }
                      secondary={
                        log.type === 'withdraw' && log.reason ? (
                          <Box sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                            사유: {log.reason}
                          </Box>
                        ) : null
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                히스토리가 없습니다.
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>닫기</Button>
        <Button 
          onClick={handleDepositSave} 
          variant="contained" 
          disabled={!depositAmount || isNaN(Number(depositAmount)) || isLocalmasterChungcheong}
        >
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChungcheongDepositManager; 